import { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { PaginationState } from '@tanstack/react-table';

import ReconciliationRunsList from 'app/components/Reconciliations/ReconciliationRunsList';

import { defaultPageSize } from 'app/constants/table';

import useFetchReconciliationsPage from 'app/hooks/useFetchReconciliationsPage';
import useQueueReconciliation from 'app/hooks/useQueueReconciliation';

import { useManagingCompanyStore, useNavigationStore } from 'app/store';

import { ReconciliationRunRow, ReconciliationRunStatus } from 'app/types/reconciliations';

import { getReconciliationProfilePath } from 'app/utils/routes';
import getReconciliationColumns from 'app/utils/table/reconciliations';

/** Lists the deduction report reconciliation runs, for every company or for the company being managed. */
function DeductionReconciliationRuns() {
  const navigate = useNavigate();
  const company = useManagingCompanyStore((state) => state.company);
  const view = useNavigationStore((state) => state.view);
  const companyId = view === 'company' ? company?.id : undefined;
  const isQueryEnabled = view === 'global' || Boolean(companyId);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [statusFilter, setStatusFilter] = useState<ReconciliationRunStatus[]>([]);

  const {
    data: runs,
    isLoading,
    isRefetching,
    refetch,
  } = useFetchReconciliationsPage(
    {
      companyId,
      status: statusFilter,
      page: pagination.pageIndex,
      size: pagination.pageSize,
    },
    isQueryEnabled
  );
  const { mutate: queueReconciliation, isPending: isQueueing } = useQueueReconciliation();

  const handleStatusChange = (statuses: ReconciliationRunStatus[]) => {
    setStatusFilter(statuses);
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
  };

  const handleSelect = (run: ReconciliationRunRow) =>
    navigate(getReconciliationProfilePath(run.id));

  return (
    <ReconciliationRunsList
      runs={runs}
      isLoading={isLoading}
      isRefreshing={isRefetching}
      onRefresh={() => refetch()}
      statusFilter={statusFilter}
      onStatusFilterChange={handleStatusChange}
      pagination={pagination}
      onPaginationChange={setPagination}
      columns={getReconciliationColumns(handleSelect)}
      companyId={companyId}
      companyName={company?.name}
      canRun={view === 'global' || Boolean(companyId)}
      isQueueing={isQueueing}
      onRun={(companyIds, onSettled) => queueReconciliation({ companyIds }, { onSettled })}
      testIdPrefix="reconciliation"
    />
  );
}

export default DeductionReconciliationRuns;
